import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { useSEO, buildBreadcrumbSchema } from '../utils/seo';
import { SEO_CITIES } from '../utils/seoCities';
import { MapPin } from 'lucide-react';

const groups = [
  { code: 'UK', label: 'United Kingdom' },
  { code: 'IN', label: 'India' },
  { code: 'CA', label: 'Canada' },
];

const LocationsHubPage = () => {
  useSEO({
    title: 'Property by City — UK · India · Canada | Shopperden',
    description: 'Browse Shopperden property listings by city. Direct-from-owner houses, flats, plots and commercial property in 20+ major UK, Indian and Canadian markets.',
    canonical: typeof window !== 'undefined' ? window.location.href : '',
    schema: [
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Locations', url: '/locations' },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Property by City — Shopperden',
        url: 'https://shopperden.com/locations',
        hasPart: SEO_CITIES.map((c) => ({
          '@type': 'WebPage',
          name: `Property in ${c.name}`,
          url: `https://shopperden.com/properties-in/${c.slug}`,
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="locations-hub">
      <Header />

      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F766E] text-white overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6 backdrop-blur-sm">
            <MapPin className="h-3 w-3 text-[#FED7AA]" />
            <p className="text-[11px] tracking-[0.28em] uppercase text-[#FED7AA] font-bold">Browse by city</p>
          </div>
          <h1 className="font-['Playfair_Display'] font-semibold leading-[1.05] tracking-tight text-[2.25rem] sm:text-5xl lg:text-[4rem]">
            <span className="block">Property in</span>
            <span className="block text-[#FED7AA] italic">every market we serve</span>
          </h1>
          <p className="mt-6 text-white/75 text-[15px] sm:text-lg max-w-2xl leading-relaxed">
            From London to Hyderabad to Toronto — Shopperden has direct-from-owner listings in 20+ major markets across the UK, India and Canada.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 lg:py-16">
        {groups.map((g) => {
          const cities = SEO_CITIES.filter((c) => c.country === g.code);
          if (!cities.length) return null;
          return (
            <div key={g.code} id={g.code.toLowerCase()} className="mb-12">
              <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A] mb-2">{g.label}</h2>
              <p className="text-sm text-[#94A3B8] mb-6 tracking-wider uppercase">{cities.length} cities</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {cities.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/properties-in/${c.slug}`}
                    className="group bg-white border border-[#E2E8F0] p-5 hover:border-[#E07A3A] hover:shadow-md transition-all"
                    data-testid={`city-link-${c.slug}`}
                  >
                    <p className="font-['Playfair_Display'] text-lg text-[#0F172A] group-hover:text-[#E07A3A] transition-colors mb-1">
                      {c.name}
                    </p>
                    <p className="text-[11px] text-[#94A3B8] tracking-wider line-clamp-1">{c.popPick}</p>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
};

export default LocationsHubPage;
